/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import { Eye, EyeOff, Mail } from "lucide-react";

export default function Signup() {
  const { register } = useAuth();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    // Validate password in real-time
    if (name === "password") {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validation
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match!");
      setLoading(false);
      return;
    }

    const passwordValidationErrors = validatePassword(form.password);
    if (passwordValidationErrors.length > 0) {
      setError("Please fix password requirements below");
      setLoading(false);
      return;
    }

    try {
      await register(form.name, form.email, form.password);
      setShowVerificationMessage(true);
    } catch (err: any) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Verification Success Screen
  if (showVerificationMessage) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center px-6 py-12 transition-colors duration-300 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <div
          className={`w-full max-w-md shadow-lg rounded-2xl p-8 border transition-colors duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-sky-100"
          }`}
        >
          <div className="text-center">
            <div
              className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full ${
                isDark ? "bg-blue-900/30" : "bg-blue-100"
              }`}
            >
              <Mail
                className={`h-8 w-8 ${
                  isDark ? "text-blue-400" : "text-blue-600"
                }`}
              />
            </div>
            <h2
              className={`mt-6 text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Check Your Email
            </h2>
            <p
              className={`mt-2 text-sm ${
                isDark ? "text-gray-400" : "text-gray-600"
              }`}
            >
              We've sent a verification link to{" "}
              <strong className={isDark ? "text-white" : "text-gray-900"}>
                {form.email}
              </strong>
            </p>
            <p
              className={`mt-2 text-sm ${
                isDark ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Please click the link in the email to verify your account and
              complete your registration.
            </p>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => setShowVerificationMessage(false)}
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-all duration-300"
              >
                Back to Sign Up
              </button>
              <button
                onClick={() => navigate("/login")}
                className={`w-full py-2 px-4 border rounded-md shadow-sm text-sm font-medium transition-all duration-300 ${
                  isDark
                    ? "border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600"
                    : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                }`}
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Signup Form
  return (
    <div
      className={`min-h-screen flex items-center justify-center px-6 py-12 transition-colors duration-300 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 to-gray-800'
          : 'bg-gradient-to-br from-sky-50 to-blue-50'
      }`}
    >
      <Helmet>
        <title>Create a Free Account | FindWork Kenya</title>
        <meta
          name="description"
          content="Join FindWork for free. Find work near you or hire workers in Kenya. No CV required — just your skills and availability."
        />
        <link rel="canonical" href="https://findwork.ltd/sign-up" />
      </Helmet>
      
      <div
        className={`w-full max-w-md shadow-lg rounded-2xl p-8 border transition-colors duration-300 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-sky-100'
        }`}
      >
        {/* Header */}
        <h1 className="text-3xl font-bold text-center bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
          Create Account
        </h1>
        <p
          className={`text-center mt-2 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          Join FindWork and land your dream job
        </p>

        {/* Error Message */}
        {error && (
          <div
            className={`mt-4 p-3 rounded-md border ${
              isDark
                ? 'bg-red-900/30 text-red-300 border-red-800'
                : 'bg-red-100 text-red-700 border-red-200'
            }`}
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {/* Full Name */}
          <div>
            <label
              className={`block font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-sky-200 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="John Doe"
            />
          </div>

          {/* Email */}
          <div>
            <label
              className={`block font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-sky-200 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="example@email.com"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <label
              className={`block font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-sky-200 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="Min 8 chars, upper, lower, number, special"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className={`absolute right-3 top-9 transition ${
                isDark
                  ? 'text-gray-400 hover:text-sky-400'
                  : 'text-gray-500 hover:text-sky-600'
              }`}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>

            {/* Password Requirements */}
            {form.password && (
              <div
                className={`mt-2 p-3 rounded-md border ${
                  isDark
                    ? 'bg-gray-700 border-gray-600'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <p
                  className={`text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Password must contain:
                </p>
                <ul className="text-xs space-y-1">
                  <li
                    className={`flex items-center ${
                      form.password.length >= 8
                        ? isDark
                          ? 'text-green-400'
                          : 'text-green-600'
                        : isDark
                          ? 'text-gray-400'
                          : 'text-gray-500'
                    }`}
                  >
                    <span className="mr-1">
                      {form.password.length >= 8 ? '✓' : '•'}
                    </span>
                    At least 8 characters
                  </li>
                  <li
                    className={`flex items-center ${
                      /(?=.*[a-z])/.test(form.password)
                        ? isDark
                          ? 'text-green-400'
                          : 'text-green-600'
                        : isDark
                          ? 'text-gray-400'
                          : 'text-gray-500'
                    }`}
                  >
                    <span className="mr-1">
                      {/(?=.*[a-z])/.test(form.password) ? '✓' : '•'}
                    </span>
                    One lowercase letter
                  </li>
                  <li
                    className={`flex items-center ${
                      /(?=.*[A-Z])/.test(form.password)
                        ? isDark
                          ? 'text-green-400'
                          : 'text-green-600'
                        : isDark
                          ? 'text-gray-400'
                          : 'text-gray-500'
                    }`}
                  >
                    <span className="mr-1">
                      {/(?=.*[A-Z])/.test(form.password) ? '✓' : '•'}
                    </span>
                    One uppercase letter
                  </li>
                  <li
                    className={`flex items-center ${
                      /(?=.*\d)/.test(form.password)
                        ? isDark
                          ? 'text-green-400'
                          : 'text-green-600'
                        : isDark
                          ? 'text-gray-400'
                          : 'text-gray-500'
                    }`}
                  >
                    <span className="mr-1">
                      {/(?=.*\d)/.test(form.password) ? '✓' : '•'}
                    </span>
                    One number
                  </li>
                  <li
                    className={`flex items-center ${
                      /(?=.*[@$!%*?&])/.test(form.password)
                        ? isDark
                          ? 'text-green-400'
                          : 'text-green-600'
                        : isDark
                          ? 'text-gray-400'
                          : 'text-gray-500'
                    }`}
                  >
                    <span className="mr-1">
                      {/(?=.*[@$!%*?&])/.test(form.password) ? '✓' : '•'}
                    </span>
                    One special character (@$!%*?&)
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <label
              className={`block font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Confirm Password
            </label>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-sky-200 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className={`absolute right-3 top-9 transition ${
                isDark
                  ? 'text-gray-400 hover:text-sky-400'
                  : 'text-gray-500 hover:text-sky-600'
              }`}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>

            {/* Password Match Indicator */}
            {form.confirmPassword && (
              <div className="mt-2">
                <p
                  className={`text-sm ${
                    form.password === form.confirmPassword
                      ? isDark
                        ? 'text-green-400'
                        : 'text-green-600'
                      : isDark
                        ? 'text-red-400'
                        : 'text-red-600'
                  }`}
                >
                  {form.password === form.confirmPassword
                    ? '✓ Passwords match'
                    : '✗ Passwords do not match'}
                </p>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || passwordErrors.length > 0}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-md hover:from-sky-600 hover:to-blue-700 disabled:opacity-70 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        {/* Footer */}
        <p
          className={`text-center mt-6 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          Already have an account?{' '}
          <Link
            to="/login"
            className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent font-medium hover:underline"
          >
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}

const validatePassword = (password: string) => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  if (!/(?=.*[a-z])/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  if (!/(?=.*\d)/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  if (!/(?=.*[@$!%*?&])/.test(password)) {
    errors.push(
      "Password must contain at least one special character (@$!%*?&)",
    );
  }

  return errors;
};
